import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { usePlan } from "@/hooks/usePlan";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

interface UsageData {
  usage_count: number;
  limit: number;
  remaining: number;
  resetAt: string;
  plan: string;
}

// Global event target for cross-component usage sync
const usageEvents = new EventTarget();

export function notifyUsageUpdate(usage: UsageData) {
  usageEvents.dispatchEvent(new CustomEvent("usage-update", { detail: usage }));
}

function getVisitorId(): string {
  let id = localStorage.getItem("dos_visitor_id");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("dos_visitor_id", id);
  }
  return id;
}

function formatResetTime(resetAt: string): string {
  if (!resetAt) return "";
  const diff = new Date(resetAt).getTime() - Date.now();
  if (diff <= 0) return "Resets soon";
  const hours = Math.ceil(diff / (1000 * 60 * 60));
  if (hours <= 1) return "Resets in <1h";
  return `Resets in ${hours}h`;
}

export default function UsagePill() {
  const { user } = useAuth();
  const { plan, trial } = usePlan();
  const navigate = useNavigate();
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUsage = useCallback(async () => {
    try {
      const headers: Record<string, string> = {};
      if (!user) {
        headers["x-visitor-id"] = getVisitorId();
      }
      const { data, error } = await supabase.functions.invoke("check-usage", {
        headers,
      });
      if (!error && data) {
        setUsage(data as UsageData);
      }
    } catch (e) {
      console.warn("Failed to fetch usage:", e);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage, plan]);

  // Listen for usage updates from search results
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<UsageData>).detail;
      if (detail) setUsage(detail);
    };
    usageEvents.addEventListener("usage-update", handler);
    return () => usageEvents.removeEventListener("usage-update", handler);
  }, []);

  const isSignedIn = !!user;

  // All plans have quotas — no unlimited bypass
  if (usage?.limit === 0) return null;
  if (loading && !usage) return null;

  const remaining = usage ? usage.remaining : 0;
  const limit = usage ? usage.limit : 0;
  const usageCount = usage ? usage.usage_count : 0;
  const isExhausted = remaining === 0 && limit > 0;
  const isDaily = !usage?.plan || usage.plan === "guest" || usage.plan === "free";

  // Trial-aware display for free users
  const showTrial = trial.isTrialActive && trial.trialRemaining > 0;

  return (
    <button
      onClick={() => {
        if (!isSignedIn) navigate("/auth");
        else navigate("/profile");
      }}
      className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-body font-medium transition-colors ${
        isExhausted
          ? "bg-score-low/15 text-score-low border border-score-low/20"
          : "bg-muted/40 text-muted-foreground border border-border/40 hover:border-primary/30"
      }`}
      title={isExhausted && usage ? formatResetTime(usage.resetAt) : `~${remaining} decisions remaining`}
    >
      <span>{usageCount}/{limit} {isDaily ? "today" : "this month"}</span>
      {showTrial && (
        <span className="text-primary">· {trial.trialRemaining} Pro left</span>
      )}
      {isExhausted && usage?.resetAt && (
        <span className="text-[9px]">{formatResetTime(usage.resetAt)}</span>
      )}
    </button>
  );
}
